import { useMemo } from 'react';
import { useAppStore } from '../store';
import { useReportStore } from '../store/reportStore';
import type { Transaction } from '../types';

export interface DailySpending {
  date: string;
  amount: number;
  count: number;
}

export interface UseDailySpendingOptions {
  startDate?: string;
  endDate?: string;
  categoryIds?: string[];
}

export function useDailySpending(
  options?: UseDailySpendingOptions
): DailySpending[] {
  const transactions = useAppStore((state) => state.transactions);
  const { dateRange, selectedCategoryIds } = useReportStore();

  const startDate = options?.startDate ?? dateRange.start;
  const endDate = options?.endDate ?? dateRange.end;
  const categoryIds = options?.categoryIds ?? selectedCategoryIds;

  return useMemo(() => {
    const categorySet = categoryIds.length > 0 ? new Set(categoryIds) : null;

    const dailyMap = new Map<string, { amount: number; count: number }>();

    transactions.forEach((transaction) => {
      if (transaction.type !== 'expense') return;
      if (transaction.excludedFromBudget) return;
      if (transaction.date < startDate || transaction.date > endDate) return;

      if (categorySet && transaction.categoryId) {
        if (!categorySet.has(transaction.categoryId)) return;
      }

      const existing = dailyMap.get(transaction.date) || {
        amount: 0,
        count: 0,
      };
      existing.amount += Math.abs(transaction.amount);
      existing.count += 1;
      dailyMap.set(transaction.date, existing);
    });

    return Array.from(dailyMap.entries())
      .map(([date, data]) => ({
        date,
        amount: data.amount,
        count: data.count,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [transactions, startDate, endDate, categoryIds]);
}

export function useTransactionsByDate(date: string): Transaction[] {
  const transactions = useAppStore((state) => state.transactions);

  return useMemo(
    () =>
      transactions.filter(
        (t) => t.date === date && t.type === 'expense' && !t.excludedFromBudget
      ),
    [transactions, date]
  );
}
